/**
 * Derives the Shopify form state for the subscription selector.
 *
 * @param {{
 *   purchaseType: 'once' | 'subscribe',
 *   bagCount: number,
 *   planId: string,
 *   oncePrice: string,
 *   twoBagPrice: string
 * }} input
 */
export function deriveSubscriptionState(input) {
  if (input.purchaseType === 'once') {
    return {
      sellingPlanId: '',
      quantity: 1,
      buttonText: 'Add to cart',
      deliveryPrice: input.oncePrice,
    };
  }

  const quantity = input.bagCount === 2 ? 2 : 1;
  const deliveryPrice = quantity === 2 ? input.twoBagPrice : input.oncePrice;

  return {
    sellingPlanId: input.planId,
    quantity,
    buttonText: 'Subscribe now',
    deliveryPrice,
  };
}

/**
 * Ensures Shopify receives one unambiguous selling plan value.
 *
 * @param {FormData} formData
 * @param {string} sellingPlanId
 */
export function applyBbxSellingPlanToFormData(formData, sellingPlanId) {
  if (sellingPlanId) {
    formData.set('selling_plan', sellingPlanId);
  } else {
    formData.delete('selling_plan');
  }

  return formData;
}

/**
 * Builds one Shopify Ajax Cart line for a queued product form submission.
 *
 * @param {{variantId: string, quantity: number, sellingPlanId?: string}} item
 */
export function buildBbxCartLine(item) {
  const line = {
    id: Number(item.variantId),
    quantity: item.quantity,
  };

  if (item.sellingPlanId) {
    line.selling_plan = Number(item.sellingPlanId);
  }

  return line;
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class BbxSubscriptionSelector extends HTMLElementBase {
  /** @type {Record<string, {oncePrice: string, twoBagPrice: string, plans: Array<{id: string, name: string}>}>} */
  #variantData = {};

  connectedCallback() {
    const dataElement = this.querySelector('[data-bbx-subscription-data]');
    this.#variantData = JSON.parse(dataElement?.textContent || '{}');
    this.addEventListener('change', this.#handleChange);
    this.#isolateVisualControls();
    this.#applyState();
    globalThis.queueMicrotask?.(() => {
      this.#isolateVisualControls();
      this.#applyState();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
  }

  /** @param {string | number} variantId */
  setVariant(variantId) {
    const nextVariant = this.#variantData[String(variantId)];
    if (!nextVariant) return;

    this.#isolateVisualControls();
    const frequencyInputs = Array.from(this.querySelectorAll('[data-bbx-control="bbx-frequency"]'));
    const selectedFrequencyIndex = frequencyInputs.findIndex((input) => input.checked);

    this.dataset.bbxVariantId = String(variantId);
    frequencyInputs.forEach((input, index) => {
      const nextPlan = nextVariant.plans[index];
      input.value = nextPlan?.id || '';
      input.disabled = !nextPlan;
      input.checked = index === Math.max(selectedFrequencyIndex, 0) && Boolean(nextPlan);
    });

    this.querySelectorAll('[data-bbx-once-price]').forEach((price) => {
      price.textContent = nextVariant.oncePrice;
    });
    this.querySelectorAll('[data-bbx-two-bag-price]').forEach((price) => {
      price.textContent = nextVariant.twoBagPrice;
    });

    this.#applyState();
  }

  #handleChange = (event) => {
    this.#isolateVisualControls();
    const changedControl = event.target?.closest?.('[data-bbx-control]');
    if (changedControl?.checked) {
      this.querySelectorAll(`[data-bbx-control="${changedControl.dataset.bbxControl}"]`).forEach((control) => {
        if (control !== changedControl) control.checked = false;
      });
    }
    this.#applyState();
  };

  #isolateVisualControls() {
    this.querySelectorAll('[name^="bbx-"]').forEach((control) => {
      control.dataset.bbxControl = control.getAttribute('name');
      control.removeAttribute('name');
    });
  }

  #applyState() {
    const form = this.closest('form');
    const variantId = this.dataset.bbxVariantId || '';
    if (!this.#variantData[variantId]) {
      const dataElement = this.querySelector('[data-bbx-subscription-data]');
      this.#variantData = JSON.parse(dataElement?.textContent || '{}');
    }
    const variant = this.#variantData[variantId];
    if (!form || !variant) return;

    const purchaseType =
      this.querySelector('[data-bbx-control="bbx-purchase-type"]:checked')?.value === 'subscribe'
        ? 'subscribe'
        : 'once';
    const bagCount = Number(this.querySelector('[data-bbx-control="bbx-bag-count"]:checked')?.value || 1);
    const planId = this.querySelector('[data-bbx-control="bbx-frequency"]:checked')?.value || '';
    const state = deriveSubscriptionState({
      purchaseType,
      bagCount,
      planId,
      oncePrice: variant.oncePrice,
      twoBagPrice: variant.twoBagPrice,
    });

    const panel = this.querySelector('[data-bbx-subscription-panel]');
    if (panel) panel.hidden = purchaseType !== 'subscribe';

    const productForm = this.closest('product-form-component');
    if (productForm) {
      if (purchaseType === 'subscribe') {
        productForm.dataset.bbxSubscriptionActive = 'true';
      } else {
        delete productForm.dataset.bbxSubscriptionActive;
      }
    }

    const sellingPlanInput = this.querySelector('[name="selling_plan"]');
    if (sellingPlanInput) sellingPlanInput.value = state.sellingPlanId;

    const quantityInput = form.querySelector('[name="quantity"]');
    const quantitySelector = quantityInput?.closest('quantity-selector-component');
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(state.quantity);
    } else if (quantityInput) {
      quantityInput.value = String(state.quantity);
    }

    const buttonText = form.querySelector('.add-to-cart-text__content span span');
    if (buttonText) buttonText.textContent = state.buttonText;
  }
}

if (globalThis.customElements && !globalThis.customElements.get('bbx-subscription-selector')) {
  globalThis.customElements.define('bbx-subscription-selector', BbxSubscriptionSelector);
}
